import { toast } from 'react-toastify';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { selectContact } from '../store/messageSlice';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContacts, fetchMessages, sendMessageAsync, sendMediaAsync, setContactsQuery } from '../store/messageSlice';
import { fetchSessions } from '../store/sessionSlice';
import { fetchFromWhatsApp, reactToMessage, replyToMessage, forwardMessage, editMessage } from '../api/messages';
import { MdClose } from 'react-icons/md';
import TranslationPanel from '../components/TranslationPanel';
import client from '../api/client';
import ChatComposer from '../components/ChatComposer';
import ForwardDialog from '../components/ForwardDialog';
import { listContacts } from '../api/messages';

import ChatHeader from '../components/ChatHeader';
import ChatHistory from '../components/ChatHistory';
import useChatDraft from '../hooks/useChatDraft';
import ChatList from '../components/ChatList';

export default function ChatPage() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const contactParam = searchParams.get('contact');
  const { contacts, messages, selectedContact, loading, hasOlder, olderRequest } = useSelector((s) => s.messages);
  const { items: sessions } = useSelector((s) => s.sessions);
  useEffect(() => {
    if (!contactParam) { if (selectedContact) dispatch(selectContact(null)); return; }
    if (selectedContact && String(selectedContact.id) === contactParam) return;
    const contact = contacts.find(item => String(item.id) === contactParam);
    if (contact) { dispatch(selectContact(contact)); return; }
    let attiva = true;
    listContacts({ id: contactParam, limit: 1 })
      .then(({ data }) => { const trovata = (Array.isArray(data) ? data : data.results)[0]; if (attiva && trovata) dispatch(selectContact(trovata)); })
      .catch(() => {});
    return () => { attiva = false; };
  }, [contactParam, contacts, selectedContact, dispatch]);
  const [connectionFilter, setConnectionFilter] = useState('connected');
  const userId = useSelector(state => state.auth.user?.id);
  const [messageText, setMessageText] = useChatDraft(userId, selectedContact);
  const [search, setSearch] = useState('');
  const [messageSearch, setMessageSearch] = useState('');
  const [cercaAperta, setCercaAperta] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [translationBusy, setTranslationBusy] = useState(false);
  const approvedTranslations = useRef({});
  const [error, setError] = useState('');
  const selectionRef = useRef();
  selectionRef.current = selectedContact?.id;
  const historyRef = useRef(null);
  const pinned = useRef(true);
  const latestMessage = messages[0]?.id;
  const [newMessages, setNewMessages] = useState(false);
  useEffect(() => {
    setReplyTo(null); setSelectedFile(null); setForwardMsg(null); setError(''); setMessageSearch(''); setCercaAperta(false);
    pinned.current = true; setNewMessages(false);
  }, [selectedContact?.id]);
  useEffect(() => {
    if (pinned.current && historyRef.current) historyRef.current.scrollTop = 0;
    else setNewMessages(true);
  }, [latestMessage]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // {id, waId, body, author}
  // Messaggio in modifica: il campo di scrittura contiene il suo testo e Invia lo corregge.
  const [inModifica, setInModifica] = useState(null); // {id, body}
  const modifica = msg => { setReplyTo(null); setSelectedFile(null); setInModifica({ id: msg.id, body: msg.body }); setMessageText(msg.body || ''); };
  const annullaModifica = () => { setInModifica(null); setMessageText(''); };
  useEffect(() => { setInModifica(null); }, [selectedContact?.id]);
  const [forwardMsg, setForwardMsg] = useState(null); // whatsapp_message_id to forward
  const fileInputRef = useRef(null);
  const emojiRef = useRef(null);

  useEffect(() => { dispatch(fetchSessions()); }, [dispatch]);
  // Ricerca e filtro li applica il server; mentre si scrive si aspetta un attimo.
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setContactsQuery({ q: search.trim(), connection: connectionFilter }));
      dispatch(fetchContacts({ reset: true }));
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search, connectionFilter, dispatch]);

  // Nessun sondaggio a tempo: messaggi, chat e connessioni si aggiornano
  // quando il server avvisa che qualcosa è cambiato (hooks/useSocket.js).
  // Prima erano 24 richieste al minuto con una chat aperta e ferma.
  useEffect(() => {
    if (!selectedContact) return;
    dispatch(fetchMessages(selectedContact.id));
  }, [selectedContact, dispatch]);

  // Close emoji picker on click outside
  useEffect(() => {
    const handler = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeSession = sessions.find(s => s.id === selectedContact?.connection && s.status === 'ready');
  const visibleContacts = contacts;
  const connectionLabel = c => !c.connection ? 'Archivio precedente · sola lettura' : (sessions.find(s => s.id === c.connection)?.phone_number || `Connessione ${c.connection}`);
  const isGroup = selectedContact?.is_group || selectedContact?.whatsapp_id?.includes('@g.us');

  const handleFetchFromWA = useCallback(async (limit = 50) => {
    if (!activeSession || !selectedContact || fetching) return;
    setFetching(true);
    try {
      await fetchFromWhatsApp(activeSession.id, selectedContact.whatsapp_id, limit);
      for (let i = 0; i < 5; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        dispatch(fetchMessages(selectedContact.id));
      }
    } catch (err) { setError('Impossibile recuperare i messaggi da WhatsApp.'); }
    finally { setFetching(false); }
  }, [activeSession, selectedContact, fetching, dispatch]);

  const handleSend = async () => {
    if (!activeSession || !selectedContact || sendingRef.current || translationBusy || (!messageText.trim() && !selectedFile)) return;
    sendingRef.current = true; setSending(true); setError('');
    const contactId = selectedContact.id;
    let prepared = false;
    try {
      const { data } = await client.post(`/v1/translator/chats/${contactId}/prepare-outgoing/`, { text: messageText, approved_preview: approvedTranslations.current[contactId] === messageText });
      if (data.status !== 'succeeded' || typeof data.text !== 'string') throw new Error('Translation not ready');
      if (selectionRef.current !== contactId) return;
      const outgoingText = data.text;
      prepared = true;
      if (inModifica) {
        await editMessage(inModifica.id, outgoingText);
        setInModifica(null);
        dispatch(fetchMessages(contactId));
      } else if (selectedFile) await dispatch(sendMediaAsync({ sessionId: activeSession.id, chatId: selectedContact.whatsapp_id, file: selectedFile, caption: outgoingText })).unwrap();
      else if (replyTo) {
        await replyToMessage(activeSession.id, replyTo.waId, outgoingText);
        dispatch(fetchMessages(contactId));
      } else await dispatch(sendMessageAsync({ sessionId: activeSession.id, chatId: selectedContact.whatsapp_id, message: outgoingText })).unwrap();
      // This setter belongs to the originating draft, even after changing chat.
      setMessageText('');
      if (selectionRef.current === contactId) { setSelectedFile(null); setReplyTo(null); pinned.current = true; }
    } catch (err) {
      // Una modifica rifiutata (telefono, oltre 15 minuti) ha il suo motivo: si mostra quello.
      if (inModifica && prepared && err.response?.data?.error) { setError(err.response.data.error); return; }
      if (selectionRef.current === contactId) setError(prepared ? 'Invio non confermato. La bozza è conservata: verifica lo storico prima di riprovare.' : 'Traduzione non riuscita o non ancora pronta. Nessun messaggio inviato; la bozza è conservata.');
    } finally { sendingRef.current = false; setSending(false); }
  };

  const handleTranslate = async messageId => {
    const contactId = selectedContact.id;
    try {
      const { data } = await client.post(`/v1/translator/chats/${contactId}/translate/`, { message_id: messageId });
      if (selectionRef.current !== contactId) return;
      if (data.status === 'succeeded') dispatch(fetchMessages(contactId));
      else setError('Traduzione in elaborazione. Riprova tra poco per recuperare il risultato.');
    } catch (err) { if (selectionRef.current === contactId) setError(err.response?.data?.error || 'Traduzione non disponibile. Originale conservato.'); }
  };

  const handleReact = async (waMessageId, emoji) => {
    if (!activeSession) return;
    try {
      await reactToMessage(activeSession.id, waMessageId, emoji);
      setTimeout(() => dispatch(fetchMessages(selectedContact.id)), 1000);
    } catch (err) { setError('Reazione non confermata.'); }
  };

  const handleForward = async (chatId, nome) => {
    if (!activeSession || !forwardMsg) return;
    await forwardMessage(activeSession.id, forwardMsg, chatId);
    setForwardMsg(null);
    // Prima l'inoltro si chiudeva senza dire niente: non si capiva se fosse partito.
    toast.success(nome ? `Inoltrato a ${nome}` : 'Messaggio inoltrato');
  };

  const handleEmojiClick = (emojiData) => {
    setMessageText((prev) => prev + emojiData.emoji);
    setShowEmoji(false);
  };

  return (
    <div className={`chat-shell ${selectedContact ? "chat-open" : ""}`}>
      {forwardMsg && <ForwardDialog connection={activeSession?.id} onClose={() => setForwardMsg(null)} onForward={handleForward} />}

      <ChatList visibleContacts={visibleContacts} connectionFilter={connectionFilter} setConnectionFilter={setConnectionFilter} dispatch={dispatch} setSelectedFile={setSelectedFile} setReplyTo={setReplyTo} setForwardMsg={setForwardMsg} selectedContact={selectedContact} sessions={sessions} search={search} setSearch={setSearch} connectionLabel={connectionLabel} />

      {/* Messages */}
      <div className="chat-conversation flex-1 flex flex-col min-w-0">
        {selectedContact ? (
          <>
            <ChatHeader selectedContact={selectedContact} connectionLabel={connectionLabel} dispatch={dispatch} activeSession={activeSession} handleFetchFromWA={handleFetchFromWA} fetching={fetching} onCerca={() => setCercaAperta(!cercaAperta)} />
            <ChatHistory handleTranslate={handleTranslate} messageSearch={messageSearch} setMessageSearch={setMessageSearch} cercaAperta={cercaAperta} setCercaAperta={setCercaAperta} historyRef={historyRef} pinned={pinned} setNewMessages={setNewMessages} hasOlder={hasOlder} dispatch={dispatch} selectedContact={selectedContact} messages={messages} olderRequest={olderRequest} loading={loading} fetching={fetching} activeSession={activeSession} handleFetchFromWA={handleFetchFromWA} isGroup={isGroup} setReplyTo={setReplyTo} setForwardMsg={setForwardMsg} handleReact={handleReact} onModifica={activeSession ? modifica : null} />
            {newMessages && <button className="text-sm text-brand-700 p-2" onClick={() => { historyRef.current.scrollTop = 0; pinned.current = true; setNewMessages(false); }}>↓ Vai agli ultimi messaggi</button>}
            {error && <p role="alert" className="p-3 text-sm bg-warning-bg text-warning">{error}</p>}
            {/* Modifica in corso */}
            {inModifica && (
              <div className="px-4 py-2 border-t border-line bg-brand-50 flex items-center gap-2">
                <div className="border-l-2 border-brand-500 pl-2 flex-1 text-xs min-w-0">
                  <span className="font-semibold text-brand-700">Modifica messaggio</span>
                  <p className="text-muted truncate">{inModifica.body}</p>
                </div>
                <button aria-label="Annulla la modifica" onClick={annullaModifica} className="text-muted hover:text-danger"><MdClose size={16} /></button>
              </div>
            )}
            {/* Reply preview */}
            {replyTo && (
              <div className="px-4 py-2 border-t border-line bg-brand-50 flex items-center gap-2">
                <div className="border-l-2 border-brand-500 pl-2 flex-1 text-xs">
                  <span className="font-semibold text-brand-700">{replyTo.author || 'Messaggio'}</span>
                  <p className="text-muted truncate">{replyTo.body}</p>
                </div>
                <button onClick={() => setReplyTo(null)} className="text-muted hover:text-danger"><MdClose size={16} /></button>
              </div>
            )}

            {/* File preview */}
            {selectedFile && (
              <div className="px-4 py-2 border-t border-line bg-surface-2 flex items-center gap-2">
                <span className="text-xs text-muted truncate flex-1">📎 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)</span>
                <button onClick={() => setSelectedFile(null)} className="text-muted hover:text-danger"><MdClose size={16} /></button>
              </div>
            )}

            {!activeSession && <p className="px-4 py-2 text-sm bg-warning-bg text-warning">
              {selectedContact.legacy ? 'Archivio precedente: connessione non attribuita. Consultazione disponibile, invio disabilitato.' : 'Connessione non disponibile. Puoi consultare i messaggi salvati.'}
            </p>}
            {selectedContact.connection && <TranslationPanel key={selectedContact.id} contact={selectedContact} text={messageText} setText={setMessageText} sending={sending} onBusyChange={setTranslationBusy} onApproved={text => { approvedTranslations.current[selectedContact.id] = text; }} />}
            <ChatComposer fileInputRef={fileInputRef} setSelectedFile={setSelectedFile} activeSession={activeSession} sending={sending || translationBusy} emojiRef={emojiRef} showEmoji={showEmoji} setShowEmoji={setShowEmoji} handleEmojiClick={handleEmojiClick} messageText={messageText} setMessageText={setMessageText} handleSend={handleSend} replyTo={replyTo} selectedFile={selectedFile} contactId={selectedContact?.id} />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-2">
              <p className="text-4xl">💬</p>
              <p className="text-sm text-muted">Seleziona una chat per iniziare</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
